// PlacementNode represents a node in the placement test graph
export type Answer = 'hard' | 'just right' | 'easy';
export type CEFRLevel = 'No Level' | 'A0' | 'Lower A1' | 'Upper A1' | 'Lower A2' | 'Upper A2' | 'Lower B1' | 'Upper B1' | 'Lower B2' | 'Upper B2' | 'Lower C1' | 'Upper C1' | 'C2';

// All possible node IDs in the placement test graph
export type NodeId = 
  | 'peppa' 
  | 'vid1' | 'vid2' | 'vid3' | 'vid4' | 'vid5' | 'vid6' | 'vid7' | 'vid8'
  | 'A0' | 'lowerA1' | 'upperA1' | 'lowerA2' | 'upperA2' | 'lowerB1' | 'upperB1' | 'lowerB2' | 'upperB2';

// Map node IDs to their CEFR levels
export const nodeToCEFRLevel: Record<string, CEFRLevel> = {
  'A0': 'A0',
  'lowerA1': 'Lower A1',
  'upperA1': 'Upper A1',
  'lowerA2': 'Lower A2',
  'upperA2': 'Upper A2',
  'lowerB1': 'Lower B1',
  'upperB1': 'Upper B1',
  'lowerB2': 'Lower B2',
  'upperB2': 'Upper B2'
};

export class PlacementNode {
  // Unique identifier for this node in the placement test graph
  id: NodeId;
  // Human-readable label for this node
  label: string;
  /** 
   * A mapping from user answers to the next node in the placement flow.
   * Only some answers may be present, depending on the current step.
   * For example: { 'hard': nodeA, 'just right': nodeB }
   */
  transitions: Partial<Record<Answer, PlacementNode>>;

  constructor(id: NodeId, label: string) {
    this.id = id;
    this.label = label;
    this.transitions = {};
  }

  /**
   * Sets up a transition from this node to another node based on the user's answer.
   * This defines what happens when a user selects a particular answer at this step.
   * @param answer - The user's response ('hard', 'just right', or 'easy')
   * @param nextNode - The node to transition to when this answer is selected
   */
  setTransition(answer: Answer, nextNode: PlacementNode) {
    this.transitions[answer] = nextNode;
  }

  /**
   * Gets the next node in the placement flow based on the user's answer.
   * Returns undefined if there is no transition defined for the given answer.
   * @param answer - The user's response ('hard', 'just right', or 'easy')
   * @returns The next node to visit, or undefined if no transition exists
   */
  getNext(answer: Answer): PlacementNode | undefined {
    return this.transitions[answer];
  }

  /**
   * Checks if this node is a terminal node in the placement test.
   * Terminal nodes represent final placement decisions (CEFR levels).
   * @returns true if this node has no outgoing transitions
   */
  isEndNode(): boolean {
    // A node is an end node if it has no outgoing transitions
    return Object.keys(this.transitions).length === 0;
  }

  /**
   * Gets the CEFR level associated with this node, if any.
   * Only terminal nodes (end nodes) should have CEFR levels.
   * @returns The CEFR level for this node, or undefined if not applicable
   */
  getCEFRLevel(): CEFRLevel | undefined {
    return nodeToCEFRLevel[this.id];
  }
}

// PlacementGraph manages the nodes and traversal
export class PlacementGraph {
  nodes: Record<string, PlacementNode> = {};
  startNode?: PlacementNode;

  /**
   * Adds a node to the placement test graph.
   * @param node - The node to add to the graph
   */
  addNode(node: PlacementNode) {
    this.nodes[node.id] = node;
  }

  /**
   * Sets the starting node for the placement test.
   * This is the first video/question shown to the user.
   * @param nodeId - The ID of the node to start from
   */
  setStart(nodeId: string) {
    this.startNode = this.nodes[nodeId];
  }

  /**
   * Retrieves a node from the graph by its ID.
   * @param nodeId - The ID of the node to retrieve
   * @returns The node if found, undefined otherwise
   */
  getNode(nodeId: string): PlacementNode | undefined {
    return this.nodes[nodeId];
  }

  /**
   * Traverses the graph based on the current node and user's answer.
   * This is the main method used during the placement test to move between nodes.
   * @param currentNodeId - The ID of the current node
   * @param answer - The user's response ('hard', 'just right', or 'easy')
   * @returns The next node to visit, or undefined if no transition exists
   */
  traverse(currentNodeId: string, answer: Answer): PlacementNode | undefined {
    const currentNode = this.getNode(currentNodeId);
    return currentNode?.getNext(answer);
  }

  /**
   * Finds the final placement node by following 'just right' answers from the current node.
   * This is used to determine the user's final CEFR level placement.
   * @param currentNodeId - The ID of the current node
   * @returns The final placement node, or undefined if not found
   */
  getEndNode(currentNodeId: string): PlacementNode | undefined {
    const currentNode = this.getNode(currentNodeId);
    if (!currentNode) return undefined;
    
    // If current node is already an end node, return it
    if (currentNode.isEndNode()) {
      return currentNode;
    }
    
    // Otherwise, traverse through 'just right' answers until we reach an end node
    let node: PlacementNode | undefined = currentNode;
    while (node && !node.isEndNode()) {
      node = node.getNext('just right');
    }
    return node;
  }
}

export function createPlacementGraph(): PlacementGraph {
  const graph = new PlacementGraph();

  // Create nodes (IDs and labels based on your diagram)
  const peppa = new PlacementNode('peppa', 'Peppa Pig\nLower A2');
  const vid1 = new PlacementNode('vid1', 'vid #1\nLower A1');
  const vid2 = new PlacementNode('vid2', 'vid #2\nLower A2');
  const vid3 = new PlacementNode('vid3', 'vid #3\nLower B1');
  const vid4 = new PlacementNode('vid4', 'vid #4\nUpper A1');
  const vid5 = new PlacementNode('vid5', 'vid #5\nUpper A2');
  const vid6 = new PlacementNode('vid6', 'vid #6\nLower B2');
  const vid7 = new PlacementNode('vid7', 'vid #7\nUpper B1');
  const vid8 = new PlacementNode('vid8', 'vid #8\nUpper B2');
  const a0 = new PlacementNode('A0', 'A0');
  const lowerA1 = new PlacementNode('lowerA1', 'Lower A1');
  const upperA1 = new PlacementNode('upperA1', 'Upper A1');
  const lowerA2 = new PlacementNode('lowerA2', 'Lower A2');
  const upperA2 = new PlacementNode('upperA2', 'Upper A2');
  const lowerB1 = new PlacementNode('lowerB1', 'Lower B1');
  const upperB1 = new PlacementNode('upperB1', 'Upper B1');
  const lowerB2 = new PlacementNode('lowerB2', 'Lower B2');
  const upperB2 = new PlacementNode('upperB2', 'Upper B2');

  // Set transitions (edges)
  peppa.setTransition('hard', vid1);
  peppa.setTransition('just right', vid2);
  peppa.setTransition('easy', vid3);

  vid1.setTransition('hard', a0);
  vid1.setTransition('just right', lowerA1);
  vid1.setTransition('easy', vid4);

  vid2.setTransition('hard', vid4);
  vid2.setTransition('just right', lowerA2);
  vid2.setTransition('easy', vid5);

  vid3.setTransition('hard', vid5);
  vid3.setTransition('just right', lowerB1);
  vid3.setTransition('easy', vid6);

  vid4.setTransition('hard', lowerA1);
  vid4.setTransition('just right', upperA1);
  vid4.setTransition('easy', lowerA2);

  vid5.setTransition('hard', lowerA2);
  vid5.setTransition('just right', upperA2);
  vid5.setTransition('easy', lowerB1);

  vid6.setTransition('hard', vid7);
  vid6.setTransition('just right', lowerB2);
  vid6.setTransition('easy', vid8);

  vid7.setTransition('hard', lowerB1);
  vid7.setTransition('just right', upperB1);
  vid7.setTransition('easy', lowerB2);

  vid8.setTransition('hard', lowerB2);
  vid8.setTransition('easy', upperB2);
  vid8.setTransition('just right', upperB2);

  // Add all nodes to the graph
  [peppa, vid1, vid2, vid3, vid4, vid5, vid6, vid7, vid8, a0, lowerA1, upperA1, lowerA2, upperA2, lowerB1, upperB1, lowerB2, upperB2].forEach(node => graph.addNode(node));

  // Set the start node
  graph.setStart('peppa');

  return graph;
} 